import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Dashboard, type FamilyMember, type User } from '../lib/api.js';
import { formatMoney } from '../lib/format.js';
import { Icon } from '../components/Icon.js';
import { Skeleton } from '../components/Skeleton.js';

/**
 * Admin-only, view-only screen — the disclosed counterpart to the family
 * disclosure text shown on every member's own profile. Never lets the
 * admin edit a member's data; only reads what that member's own dashboard
 * already shows them. §DEMO
 */
export function FamilyView() {
  const [members, setMembers] = useState<FamilyMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ member: User; dashboard: Dashboard } | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  useEffect(() => {
    api.family.members()
      .then(setMembers)
      .catch(() => setError('אין לך הרשאה לצפות במסך הזה, או שהמשפחה לא נמצאה.'));
  }, []);

  const openMember = async (id: string) => {
    setLoadingId(id);
    try {
      const result = await api.family.memberDashboard(id);
      setSelected(result);
    } catch {
      setError('לא הצלחנו לטעון את הנתונים של בן המשפחה הזה.');
    } finally {
      setLoadingId(null);
    }
  };

  if (error) {
    return (
      <main className="screen stack">
        <p className="label" style={{ color: 'var(--danger)' }}>{error}</p>
        <Link to="/profile" className="btn">חזרה לפרופיל</Link>
      </main>
    );
  }

  if (!members) {
    return (
      <main className="screen stack">
        <Skeleton height={64} radius={16} />
        <Skeleton height={64} radius={16} />
        <Skeleton height={64} radius={16} />
      </main>
    );
  }

  if (selected) {
    return (
      <main className="screen stack">
        <button className="btn" onClick={() => setSelected(null)} style={{ alignSelf: 'flex-start' }}>
          <Icon name="chevron" size={16} /> חזרה לרשימה
        </button>
        <div className="stack" style={{ gap: 'var(--sp-1)' }}>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>{selected.member.name ?? selected.member.email}</h1>
          <p className="subtle label--tiny">צפייה בלבד — הנתונים זהים למה שבן המשפחה רואה בעצמו.</p>
        </div>
        <div className="card stack">
          <span className="card-title">פנוי להוצאה</span>
          <span style={{ fontSize: 28, fontWeight: 700 }}>{formatMoney(selected.dashboard.safeToSpend.amount)}</span>
        </div>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
          <div className="card stack">
            <span className="card-title">שווי נטו</span>
            <span style={{ fontSize: 20, fontWeight: 600 }}>{formatMoney(selected.dashboard.position.netWorth)}</span>
          </div>
          <div className="card stack">
            <span className="card-title">הוצאות החודש</span>
            <span style={{ fontSize: 20, fontWeight: 600 }}>{formatMoney(selected.dashboard.month.expenses)}</span>
          </div>
          <div className="card stack">
            <span className="card-title">הכנסות החודש</span>
            <span style={{ fontSize: 20, fontWeight: 600 }}>{formatMoney(selected.dashboard.month.income)}</span>
          </div>
          <div className="card stack">
            <span className="card-title">נחסך החודש</span>
            <span style={{ fontSize: 20, fontWeight: 600 }}>{formatMoney(selected.dashboard.month.saved)}</span>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="screen stack">
      <div className="stack" style={{ gap: 'var(--sp-1)' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>בני המשפחה</h1>
        <p className="subtle label--tiny">
          צפייה בלבד — לא ניתן לערוך נתונים של אף בן משפחה מכאן. כל בן משפחה רואה במסך הפרופיל שלו שיש למנהל/ת גישת צפייה זו.
        </p>
      </div>
      <div className="stack">
        {members.map((m) => (
          <button
            key={m.id}
            className="card"
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', textAlign: 'start' }}
            onClick={() => void openMember(m.id)}
            disabled={loadingId === m.id}
          >
            <span className="stack" style={{ gap: 2 }}>
              <span style={{ fontWeight: 600 }}>{m.name ?? m.email}</span>
              <span className="label--tiny subtle">{m.familyRole === 'family_admin' ? 'מנהל/ת המשפחה' : 'בן/בת משפחה'}</span>
            </span>
            {loadingId === m.id ? (
              '…'
            ) : m.summary ? (
              <span style={{ fontWeight: 600 }}>{formatMoney(m.summary.safeToSpend)}</span>
            ) : (
              <Icon name="chevron-down" size={16} />
            )}
          </button>
        ))}
      </div>
    </main>
  );
}
